/* MASNUAT Real Life. Shopify owns prices, inventory, cart and checkout. */
(() => {
  'use strict';
  const raw = document.getElementById('MasnuatConfig');
  if (!raw) return;
  const config = JSON.parse(raw.textContent);
  document.documentElement.classList.add('js');
  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const publish = (name, data) => {
    // No standard commerce events are synthesised here; Shopify/app pixels own them.
    if (!config.analytics) return;
    const shopify = window.Shopify;
    if (!shopify?.customerPrivacy?.analyticsProcessingAllowed?.()) return;
    try {
      const result = shopify.analytics?.publish?.(name, data);
      result?.catch?.(() => {});
    } catch (_) { /* Analytics must never interrupt shopping. */ }
  };

  function init(root = document) {
    root.querySelectorAll('[data-gallery]').forEach(gallery => {
      if (gallery.dataset.ready) return;
      gallery.dataset.ready = 'true';
      const track = gallery.querySelector('[data-track]');
      const slides = [...track.querySelectorAll('[data-slide]')];
      if (!slides.length) return;
      let index = 0;
      const previous = gallery.querySelector('[data-prev]');
      const next = gallery.querySelector('[data-next]');
      const number = gallery.querySelector('[data-slide-number]');
      const update = () => {
        const origin = track.getBoundingClientRect().left;
        let closest = Infinity;
        slides.forEach((slide, i) => {
          const distance = Math.abs(slide.getBoundingClientRect().left - origin);
          if (distance < closest) { closest = distance; index = i; }
        });
        if (number) number.textContent = String(index + 1);
        if (previous) previous.disabled = index === 0;
        if (next) next.disabled = index === slides.length - 1;
      };
      const move = target => {
        const slide = slides[Math.max(0, Math.min(slides.length - 1, target))];
        track.scrollBy({left: slide.getBoundingClientRect().left - track.getBoundingClientRect().left, behavior: reduced() ? 'instant' : 'smooth'});
      };
      previous?.addEventListener('click', () => move(index - 1));
      next?.addEventListener('click', () => move(index + 1));
      track.addEventListener('scroll', update, {passive: true});
      gallery.addEventListener('masnuat:show-block', event => {
        const target = slides.findIndex(slide => slide.dataset.blockId === event.detail);
        if (target >= 0) move(target);
      });
      update();
    });
    root.querySelectorAll('[data-campaign-rail]').forEach(rail => {
      rail.tabIndex = 0;
    });
  }

  const dialog = document.getElementById('QuickShop');
  let quickRequest;
  let trigger;
  document.addEventListener('click', async event => {
    const link = event.target.closest('a[data-quick-shop]');
    if (!link || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button !== 0 || !dialog?.showModal) return;
    event.preventDefault();
    trigger = link;
    quickRequest?.abort();
    const controller = new AbortController();
    quickRequest = controller;
    const target = dialog.querySelector('[data-quick-content]');
    target.textContent = config.loading;
    if (!dialog.open) dialog.showModal();
    try {
      const url = new URL(link.href, location.origin);
      url.searchParams.set('section_id', 'quick-shop');
      const response = await fetch(url, {signal: controller.signal, credentials: 'same-origin'});
      if (!response.ok) throw new Error('Section unavailable');
      const html = await response.text();
      if (controller.signal.aborted || !dialog.open) return;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const product = parsed.querySelector('.quick-product');
      if (!product || !product.querySelector('form')) throw new Error('No product form');
      target.replaceChildren(product);
      publish('masnuat_look_opened', {product_id: link.dataset.productId, product_url: link.href});
    } catch (error) {
      if (error.name !== 'AbortError' && dialog.open) location.assign(link.href);
    }
  });
  dialog?.querySelector('[data-close-dialog]')?.addEventListener('click', () => dialog.close());
  dialog?.addEventListener('click', event => {
    if (event.target !== dialog) return;
    const rect = dialog.getBoundingClientRect();
    if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
  });
  dialog?.addEventListener('close', () => { quickRequest?.abort(); trigger?.focus(); });

  document.addEventListener('change', event => {
    const select = event.target.closest('[data-variant]');
    if (!select) return;
    const box = select.closest('[data-buy-box]');
    const option = select.selectedOptions[0];
    const available = option?.dataset.available === 'true';
    const button = box.querySelector('[data-add]');
    if (button) { button.disabled = !available; button.textContent = available ? config.add : config.soldOut; }
    box.querySelector('[data-price]').textContent = option?.dataset.price || '';
    box.querySelector('[data-status]').textContent = '';
    box.querySelector('[data-added-actions]').hidden = true;
    if (select.closest('[data-product-page]')) {
      const url = new URL(location.href);
      url.searchParams.set('variant', select.value);
      history.replaceState({}, '', url);
    }
    publish('masnuat_variant_selected', {product_id: box.dataset.productId, variant_id: select.value});
  });

  document.addEventListener('submit', async event => {
    const form = event.target;
    if (!form.matches('form.buy-form') || !window.fetch) return;
    event.preventDefault();
    if (form.dataset.busy === 'true') return;
    const box = form.closest('[data-buy-box]');
    const select = form.querySelector('[data-variant]');
    const button = form.querySelector('[data-add]');
    const status = form.querySelector('[data-status]');
    if (!button || select.selectedOptions[0]?.dataset.available !== 'true') return;
    const body = new FormData(form); // Capture variant before disabling controls.
    form.dataset.busy = 'true';
    form.setAttribute('aria-busy', 'true');
    button.disabled = true;
    select.disabled = true;
    status.textContent = config.loading;
    let success = false;
    try {
      const response = await fetch(config.cartAdd, {method: 'POST', body, credentials: 'same-origin', headers: {'Accept': 'application/json'}});
      const result = await response.json();
      if (!response.ok) throw new Error(typeof result.description === 'string' ? result.description : config.error);
      success = true;
      document.dispatchEvent(new CustomEvent('masnuat:added', {detail: {trigger: button}}));
      status.textContent = config.added;
      box.querySelector('[data-added-actions]').hidden = false;
      // Count refresh is independent of the successful add. Never replay cart mutations.
      fetch(config.cart, {credentials: 'same-origin', headers: {'Accept': 'application/json'}})
        .then(r => { if (!r.ok) throw new Error(); return r.json(); })
        .then(cart => document.querySelectorAll('[data-cart-count]').forEach(node => { node.textContent = String(cart.item_count); }))
        .catch(() => {});
    } catch (error) {
      status.textContent = error instanceof TypeError || error instanceof SyntaxError ? config.networkError : (error.message || config.error);
    } finally {
      form.dataset.busy = 'false';
      form.removeAttribute('aria-busy');
      select.disabled = false;
      button.disabled = select.selectedOptions[0]?.dataset.available !== 'true';
      if (!success) box.querySelector('[data-added-actions]').hidden = false; // Bag is accessible after uncertain network outcome.
    }
  });
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:block:select', event => {
    event.target.closest('[data-gallery]')?.dispatchEvent(new CustomEvent('masnuat:show-block', {detail: event.detail.blockId}));
  });
  init();
})();
